// CSRF protection utilities
import { getAuthToken } from './useSecureAuth';

// Get CSRF token from server
export const getCSRFToken = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      // If no auth token is available, we can't fetch CSRF token
      // This is expected during initial login flow
      throw new Error('NO_AUTH_TOKEN');
    }
    
    const response = await fetch('/api/csrf-token', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }
    
    const data = await response.json();
    return data.csrfToken;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    // Return null instead of throwing an error to allow graceful handling
    if (error.message === 'NO_AUTH_TOKEN') {
      throw new Error('NO_AUTH_TOKEN');
    }
    return null;
  }
};

// Secure fetch wrapper that includes CSRF token
export const secureFetch = async (url, options = {}) => {
  try {
    // Get CSRF token
    const csrfToken = await getCSRFToken();
    
    // If we don't have a CSRF token, we can't make a secure request
    if (!csrfToken) {
      throw new Error('Unable to obtain CSRF token. Please refresh the page and try again.');
    }
    
    // Add CSRF token to headers
    const headers = {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    // Add authorization token if available
    const authToken = getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Make the request
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    return response;
  } catch (error) {
    console.error('Secure fetch error:', error);
    // Re-throw specific errors
    if (error.message === 'NO_AUTH_TOKEN') {
      throw new Error('Authentication required for this operation');
    }
    throw error;
  }
};

// CSRF-protected form submit function
export const submitFormWithCSRF = async (url, formData) => {
  try {
    const response = await secureFetch(url, {
      method: 'POST',
      body: JSON.stringify(formData)
    });
    
    return {
      success: response.ok,
      status: response.status,
      data: response.ok ? await response.json() : null,
      error: response.ok ? null : 'Form submission failed'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Form submission failed'
    };
  }
};

export default {
  getCSRFToken,
  secureFetch,
  submitFormWithCSRF
};